/**
 * React hooks for selecting values from Valtio proxy stores.
 *
 * Choose the narrowest hook that fits:
 *
 * | Hook                  | Subscribes to | Use when                        |
 * | --------------------- | ------------- | ------------------------------- |
 * | `useStoreKeyEquals`   | One key       | Comparing that key with a value |
 * | `useStoreKeySelector` | One key       | Selecting a value from that key |
 * | `useStoreSelector`    | Entire store  | Selecting from multiple keys    |
 *
 * React compares selector results with `Object.is`. Return a primitive or
 * another referentially stable value. Do not create a new object or array on
 * every call.
 *
 * Key-based hooks detect replacement of the subscribed top-level property.
 * For object-valued properties, replace the property instead of only mutating
 * data inside it:
 *
 * @example
 *   // Detected by a subscription to `selection`.
 *   gridState.selection = { type: 'cell', cellKey }
 *
 *   // Do not rely on a key subscription to detect this.
 *   gridState.selection.cellKey = cellKey
 *
 *   Pass the Valtio proxy itself to these hooks, not a snapshot.
 */
import { useCallback, useSyncExternalStore } from 'react'
import { subscribe } from 'valtio/vanilla'
import { subscribeKey } from 'valtio/vanilla/utils'

/**
 * Returns whether one top-level store property strictly equals `value`.
 *
 * Only changes to `key` trigger a new comparison. The component re-renders
 * only when the boolean result changes.
 *
 * @example
 *   const isEditing = useStoreKeyEquals(gridState, 'editingCell', cellKey)
 *
 * @param store Valtio proxy store.
 * @param key Top-level property to subscribe to.
 * @param value Value to compare with `store[key]` using `===`.
 */
export function useStoreKeyEquals<T extends object, K extends keyof T>(
  store: T,
  key: K,
  value: T[K],
): boolean {
  const subscribeToKey = useCallback(
    (onStoreChange: () => void) =>
      subscribeKey(store, key, onStoreChange, true),
    [store, key],
  )

  const getSnapshot = useCallback(
    () => store[key] === value,
    [store, key, value],
  )

  return useSyncExternalStore(subscribeToKey, getSnapshot)
}

/**
 * Selects a value from one top-level store property.
 *
 * Only changes to `key` run the selector. The component re-renders only when
 * the selected result changes according to `Object.is`.
 *
 * @example
 *   const isSelected = useStoreKeySelector(
 *     gridState,
 *     'selection',
 *     (selection) =>
 *       selection?.type === 'cell' && selection.cellKey === cellKey,
 *   )
 *
 * @example
 *   const selectionLabel = useStoreKeySelector(
 *     gridState,
 *     'selection',
 *     (selection) => (selection ? 'Selected' : 'Nothing selected'),
 *   )
 *
 * @param store Valtio proxy store.
 * @param key Top-level property to subscribe to.
 * @param selector Function that selects a value from the property.
 */
export function useStoreKeySelector<T extends object, K extends keyof T, R>(
  store: T,
  key: K,
  selector: (value: T[K]) => R,
): R {
  const subscribeToKey = useCallback(
    (onStoreChange: () => void) =>
      subscribeKey(store, key, onStoreChange, true),
    [store, key],
  )

  const getSnapshot = () => selector(store[key])

  return useSyncExternalStore(subscribeToKey, getSnapshot)
}

/**
 * Selects a value from any part of a store.
 *
 * The selector runs whenever the store subscription is notified. The
 * component re-renders only when the result changes according to `Object.is`.
 * Keep frequently used selectors inexpensive.
 *
 * @example
 *   const isSelected = useStoreSelector(gridState, (state) =>
 *     state.selectedCells.has(cellKey),
 *   )
 *
 * @example
 *   const isActiveRow = useStoreSelector(
 *     gridState,
 *     (state) =>
 *       state.selectedCell !== null && rowOf(state.selectedCell) === rowIndex,
 *   )
 *
 * @param store Valtio proxy store.
 * @param selector Function that selects a value from the store.
 */
export function useStoreSelector<T extends object, R>(
  store: T,
  selector: (store: T) => R,
): R {
  const subscribeToStore = useCallback(
    (onStoreChange: () => void) => subscribe(store, onStoreChange, true),
    [store],
  )

  const getSnapshot = () => selector(store)

  return useSyncExternalStore(subscribeToStore, getSnapshot)
}
